import { useCallback, useMemo, useRef } from 'react';
import AxiosService from '__services/AxiosService';
import { useTranslation } from 'react-i18next';
import { useSelectTypeaheadWithScroll } from '__components/Inputs/Select/hooks';
import { EMPTY_ARRAY } from 'utility/fieldHelpers';
import { SelectNoScrollCapture as Select } from '__components/Inputs/Select';
import { MultiSelectNoScrollCapture as MultiSelect } from '__components/Inputs/MultiSelect';
import { GenericOption } from '__pages/SmartConnectors/types';
import { searchEntityRecords } from '@kizen/api/records';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { useSelector } from 'react-redux';
import { entityRecordsToOption } from './entityRecordsToOption';

type RecordSelectProps = {
  customObjectId: string;
  label: string | undefined;
  labelInfo?: string;
  error: boolean;
  validate: any;
  variant: 'underline' | 'outline';
  menuInline?: boolean;
  menuLeftButton?: any;
  menuRightButton?: any;
  margin?: boolean | number | 'dense' | 'normal';
  isArray: boolean;
  value: GenericOption | GenericOption[] | null;
  onChange: (value: GenericOption | GenericOption[]) => void;
};

export const RecordSelect = ({
  customObjectId,
  value,
  onChange,
  label,
  labelInfo,
  error,
  validate,
  variant,
  menuInline,
  menuLeftButton,
  menuRightButton,
  margin = true,
  isArray = false,
  ...rest
}: RecordSelectProps) => {
  const { t } = useTranslation();

  const selectRef = useRef(null);

  const params = useMemo(
    () => ({
      active: true,
      custom_object_id: customObjectId,
    }),
    [customObjectId]
  );
  const contactObject = useSelector(getBusinessClientObject);

  const searchRecords = useCallback(
    ({ search, page }: { search: string; page: number }) => {
      return customObjectId
        ? searchEntityRecords(AxiosService, customObjectId, {
            params: {
              page_size: 20,
              page,
              search,
              ordering:
                customObjectId === contactObject.id ? 'full_name' : 'name',
            },
          })
        : { count: 0, results: [], next: null, previous: null };
    },
    [customObjectId, contactObject]
  );

  const [typeaheadProps, { loading }] = useSelectTypeaheadWithScroll({
    enabled: !!customObjectId,
    fetch: searchRecords,
    selectRef,
    params,
    chosenValueIds: EMPTY_ARRAY,
    objectToOption: entityRecordsToOption(t),
    keepPreviousData: false,
  });

  const props = {
    ref: selectRef,
    value,
    label,
    labelInfo,
    labelInfoPlacement: 'top',
    labelInfoMaxWidth: 255,
    onChange,
    placeholder: isArray ? t('Select Records') : t('Select Record'),
    ...typeaheadProps,
    isLoading: loading,
    error,
    validate,
    variant,
    menuInline,
    menuLeftButton,
    menuRightButton,
    margin,
    ...rest,
  };

  return isArray ? <MultiSelect {...props} /> : <Select {...props} />;
};
